import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ChartPanel from './ChartPanel';
import InteractiveChart from './InteractiveChart';
import FundamentalsChart from './FundamentalsChart';
import BrokerProfile from './BrokerProfile';
import ClosedTradesPage from './ClosedTradesPage';
import FinancialsTable from './FinancialsTable';
import PositionsPage from './PositionsPage';
import OrdersPage from './OrdersPage';
import QuickOrderBar from './QuickOrderBar';
import DraggableBox from './DraggableBox';
import DraggableFab from './DraggableFab';
import WatchlistAlerts from './WatchlistAlerts';
import DataHealthBanner from './DataHealthBanner';
import StockInfoPanel from './StockInfoPanel';
import { apiFetch } from './api';
import { prefetchChart, invalidateChart } from './chartCache';
import SplitAdjustBox from './SplitAdjustBox';
import { prefetchNews } from './newsCache';
import './MainApp.css';

// NSE stocks with active F&O (futures & options) contracts — Aug 2026
const FNO_TICKERS = new Set([
  '360ONE','ABB','ABBOTINDIA','ADANIENT','ADANIGREEN','ADANIPORTS','ADANIPOWER',
  'ADANIEENS','ABCAPITAL','ABFRL','ALKEM','AMBER','AMBUJACEM','ANGELONE',
  'APLAPOLLO','APOLLOHOSP','ASHOKLEY','ASIANPAINT','ASTRAL','ATUL','AUBANK',
  'AUROPHARMA','AXISBANK','BAJAJ-AUTO','BAJAJFINSV','BAJAJHLDNG','BAJFINANCE',
  'BALKRISIND','BANARISUT','BANDHANBNK','BANKBARODA','BANKINDIA','BATAINDIA',
  'BEL','BHARATFORG','BHARTIARTL','BHEL','BIOCON','BOSCHLTD','BPCL',
  'BRITANNIA','BSOFT','CAMS','CANBK','CDSL','CENTURYPLY','CHAMBLFERT',
  'CHOLAFIN','CIPLA','COALINDIA','COCHINSHIP','COFORGE','COLPAL','CONCOR',
  'COROMANDEL','CROMPTON','CROMPTON','CUMMINSIND','DALBHARAT','DABUR',
  'DEEPAKNTR','DELHIVERY','DEVYANI','DIVISLAB','DLF','DRREDDY','EASEMYTRIP',
  'EICHERMOT','ESCORTS','ETERNAL','FEDERALBNK','FINCABLES','FORTIS',
  'GAIL','GLENMARK','GMRINFRA','GODREJAGRO','GODREJCP','GODREJPROP',
  'GRASIM','HAL','HAVELLS','HCLTECH','HDFCBANK','HDFCLIFE','HDFCAMC',
  'HEROMOTOCO','HINDALCO','HINDCOPPER','HINDPETRO','HINDUNILVR','HONAUT',
  'ICICIBANK','ICICIGI','ICICIPRULI','IDEA','IEX','INDHOTEL','INDIANB',
  'INDIGO','INDUSINDBK','INDUSTOWER','INFY','IOC','IRCTC','IRFC','ITC',
  'JINDALSTEL','JIOFIN','JSWENERGY','JSWSTEEL','KAJARIACER','KALYANKJIL',
  'KAYNES','KEI','KPITTECH','KOTAKBANK','KPITTECH','LAURUSLABS','LEMONTREE',
  'LICHSGFIN','LT','LTF','LUPIN','MANAPPURAM','MARICO','MARUTI','MAZDOCK',
  'MCX','METROPOLIS','MGL','MIDHANI','MOTHERSON','MOTILALOFS','MPHASIS',
  'MUTHOOTFIN','NAM-INDIA','NATIONALUM','NAVINFLUOR','NBCC','NCC',
  'NESTLEIND','NETWORK18','NHPC','NMDC','NTPC','NYKAA','OBEROIRLTY',
  'OFSS','OIL','ONMOBILE','ONGC','PAGEIND','PATANJALI','PETRONET',
  'PHOENIXLTD','PIDILITIND','PIIND','PNB','PNBHOUSING','POLYCAB',
  'POWERGRID','PVRINOX','RAJESHEXPO','RAMCOCEM','RBLBANK','RECLTD',
  'RELIANCE','RVNL','SAIL','SBICARD','SBILIFE','SBIN','SHOPERSTOP',
  'SHRIRAMFIN','SIEMENS','SOLARINDS','SONACOMS','SRF','SUNPHARMA',
  'SUNTV','SUPREMEIND','SYNGENE','TATACHEM','TATACOMM','TATACONSUM',
  'TATAELXSI','TATAMOTORS','TATAPOWER','TATASTEEL','TCS','TECHM',
  'TORNTPHARM','TRENT','TATAINVEST','TVSMOTOR','UBL','ULTRACEMCO',
  'UNIONBANK','UPL','VEDL','VOLTAS','WAAREE','WELCORP','WELSPUNIND',
  'WHIRLPOOL','WIPRO','YESBANK','ZEEL','ZYDUSLIFE','CAMPUS','DELHIVERY',
  'EMAMIL','FLUOROCHEM','HONAUT','INDIACEM','INDIAMART','JUBLFOOD',
  'LALPATHLAB','MAXHEALTH','OLECTRA','PERSISTENT','PRESTIGE','RAJESHEXPO',
  'SBICARD','TATVA','VGUARD','VISHALMEGA','ZYDUSWELL'
]);

const numberText = (value, digits = 2) => {
  if (value === '' || value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

// Room left in today's average range: the stock's ATR as a % of price, minus
// how far it has already moved today (direction-agnostic). Negative means it
// has already travelled further than an average day. Computed here rather than
// read from the row's stored `sl`, which snapshots at a different moment and
// so disagrees with the ATR%/1D% shown beside it about half the time.
const slPercent = (row) => {
  const atrPct = Number(row.atr_pct);
  const move = Number(row.r1d);
  if (!Number.isFinite(atrPct) || !Number.isFinite(move)) return null;
  return atrPct - Math.abs(move);
};

const volumeText = (value) => {
  if (value === '' || value === null || value === undefined) return '—';
  const n = Number(value);
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString('en-GB');
};

// Date in the Indian trading timezone, used to key the daily viewed set so it
// resets on the Indian calendar date rather than the browser's local one.
const kolkataDate = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const get = (type) => (parts.find((part) => part.type === type) || {}).value;
  return `${get('year')}-${get('month')}-${get('day')}`;
};

const lowReturnFor = (row, group) => {
  if (group === '1mth_30pc') return row.low_return_1m_pct;
  if (group === '3mth_50pc') return row.low_return_3m_pct;
  if (group === '6mth_50pc') return row.low_return_6m_pct;
  if (group === 'momentum_merged' || group === 'momentum_coiled' || group === 'momentum_springout') {
    if (row['1mth_30pc']) return row.low_return_1m_pct;
    if (row['3mth_50pc']) return row.low_return_3m_pct;
    if (row['6mth_50pc']) return row.low_return_6m_pct;
  }
  return row.low_return_1m_pct;
};

const lowReturnLabelFor = (group) => ({
  '1mth_30pc': '1m%',
  '3mth_50pc': '3m%',
  '6mth_50pc': '6m%',
  momentum_merged: 'Low%',
  momentum_coiled: 'Low%',
  momentum_springout: 'Low%',
}[group] || '1m%');

const EMPTY_TRADING_STATE = {
  brokers: [],
  positions: [],
  closed_trades: [],
  voided_trades: [],
  summary: {
    realised_pnl: 0, unrealised_pnl: 0, total_pnl: 0,
    wins: 0, losses: 0, open_trades: 0, closed_trades: 0,
    success_rate: 0, by_broker: [],
  },
};

// Where the app opens, and where it comes back to.
//
// A fresh login lands on Springout. The current list and page are then kept in
// sessionStorage, so a refresh (or tabbing away and back) returns to exactly
// where you were, while opening the app again later starts from the default.
const DEFAULT_ADMIN_GROUP = 'momentum_springout';
const GROUP_KEY = 'bse2Group';
const PAGE_KEY = 'bse2Page';
const TRADING_PAGES = ['positions', 'orders', 'closed'];

const readSession = (key) => {
  try { return sessionStorage.getItem(key) || ''; } catch (_error) { return ''; }
};
const writeSession = (key, value) => {
  try { sessionStorage.setItem(key, value); } catch (_error) { /* private mode */ }
};

const restoreGroup = (isAdmin) => readSession(GROUP_KEY)
  || (isAdmin ? DEFAULT_ADMIN_GROUP : 'all_tickers');

// A restored page must still be one this account can open: a saved trading
// page would otherwise strand a user whose permission has since been removed.
const restorePage = (canTrade) => {
  const saved = readSession(PAGE_KEY);
  if (saved === 'screener') return saved;
  if (TRADING_PAGES.includes(saved)) return canTrade ? saved : 'screener';
  return 'screener';
};

const MainApp = ({ username, isAdmin = false, canTrade = false, riskPerTrade = 200, onUpdated, onManageUsers, onLogout }) => {
  const [page, setPage] = useState(() => restorePage(canTrade));
  const [toolbarOpen, setToolbarOpen] = useState(false);
  const [screenLauncherOpen, setScreenLauncherOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState(() => restoreGroup(isAdmin));
  // momentum_merged narrowed to names that rose after one or two quiet days.
  const isCoiledGroup = group === 'momentum_coiled';
  // momentum_merged narrowed to names returning to their 10MA after a run
  // clearly above it.
  const isSpringoutGroup = group === 'momentum_springout';
  const [rows, setRows] = useState([]);
  // Taken from the rows the backend returned rather than recomputed here, so
  // the label can never disagree with the numbers beside it.
  const coilAsOf = rows.find((row) => row.coil_as_of)?.coil_as_of || '';
  const springAsOf = rows.find((row) => row.spring_as_of)?.spring_as_of || '';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sort, setSort] = useState({ key: 'r1d', direction: 'desc' });
  const [selectedLink, setSelectedLink] = useState(() => localStorage.getItem('bse2SelectedLink') || '');
  const [chartPeriod, setChartPeriod] = useState('daily');
  const [chartMode, setChartMode] = useState('interactive');
  // Quarterly figures under the chart are hidden by default — clicking the
  // chart toggles them, since they're only occasionally needed.
  const [showFigures, setShowFigures] = useState(false);
  const [chartReloadKey, setChartReloadKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState('');
  const [showSplitBox, setShowSplitBox] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchedStock, setSearchedStock] = useState(null);
  const tickerLookupRef = useRef(null);
  const [tickerLookupDropdownStyle, setTickerLookupDropdownStyle] = useState(null);
  const [tradingState, setTradingState] = useState(EMPTY_TRADING_STATE);
  const [tradingBusy, setTradingBusy] = useState(false);
  const [tradingError, setTradingError] = useState('');
  const [tradingMessage, setTradingMessage] = useState('');
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistBusy, setWatchlistBusy] = useState(false);
  const [watchlistMessage, setWatchlistMessage] = useState('');
  const [stopPickMode, setStopPickMode] = useState(false);
  // Lifted (not owned by QuickOrderBar) so the chart can draw the same
  // entry/stop as a price line - matching what you're about to submit,
  // same pattern as finviz2.
  // The floating order box is hidden unless switched on with the chart's O button.
  const [showOrderBox, setShowOrderBox] = useState(() => {
    try { return localStorage.getItem('bse5-show-order-box') === '1'; } catch { return false; }
  });
  const toggleOrderBox = () => setShowOrderBox((shown) => {
    try { localStorage.setItem('bse5-show-order-box', shown ? '0' : '1'); } catch {}
    return !shown;
  });
  const [orderEntryPrice, setOrderEntryPrice] = useState('');
  const [orderStopPrice, setOrderStopPrice] = useState('');

  // Live "viewed" ticks: marking a stock as reviewed today. Keyed by date so
  // it resets itself each day, but stored server-side so it is shared across
  // the user's devices (mirrors the screener2 New filter).
  const viewedKey = `bse4-viewed-${kolkataDate()}`;
  const [viewedSymbols, setViewedSymbols] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(viewedKey) || '[]')); } catch { return new Set(); }
  });
  // Minimum ADR% a row must meet to remain visible in any list. Default 4.5.
  const [adrThreshold, setAdrThreshold] = useState(() => {
    const saved = localStorage.getItem('bse2AdrThreshold');
    return saved !== null ? Number(saved) : 4.5;
  });
  const [showOnlyUnticked, setShowOnlyUnticked] = useState(() => {
    const key = `bse4-show-only-unticked-${String(username || '').trim().toLowerCase()}`;
    try { return localStorage.getItem(key) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(`bse4-show-only-unticked-${String(username || '').trim().toLowerCase()}`, showOnlyUnticked ? '1' : '0'); } catch {}
  }, [showOnlyUnticked, username]);
  useEffect(() => {
    try { localStorage.setItem('bse2AdrThreshold', String(adrThreshold)); } catch {}
  }, [adrThreshold]);
  useEffect(() => {
    let loaded = new Set();
    try { loaded = new Set(JSON.parse(localStorage.getItem(viewedKey) || '[]')); } catch {}
    setViewedSymbols(loaded);
    apiFetch(`/api/viewed?date=${encodeURIComponent(viewedKey)}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => {
        if (Array.isArray(body?.symbols)) setViewedSymbols(new Set(body.symbols));
      })
      .catch(() => {});
  }, [viewedKey]);
  const toggleViewed = (symbol) => {
    const viewed = !viewedSymbols.has(symbol);
    setViewedSymbols((current) => {
      const next = new Set(current);
      if (viewed) next.add(symbol);
      else next.delete(symbol);
      try { localStorage.setItem(viewedKey, JSON.stringify([...next])); } catch {}
      return next;
    });
    apiFetch(`/api/viewed/${encodeURIComponent(symbol)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date: viewedKey, viewed }),
    }).catch(() => {});
    // Marking a ticker viewed is the end of its review. Move directly to the
    // next visible ticker so the next chart is loaded without another click.
    if (viewed) selectNextAfterViewed(symbol);
  };

  const selectNextAfterViewed = (symbol) => {
    const currentIndex = visibleRows.findIndex((row) => row.lnk === symbol);
    const nextRow = currentIndex >= 0 ? visibleRows[currentIndex + 1] : null;
    if (!nextRow) return;
    selectStock(nextRow.lnk);
    window.requestAnimationFrame(() => {
      const container = tableScrollRef.current;
      const nextRowElement = container && [...container.querySelectorAll('tbody tr')]
        .find((element) => element.dataset.link === nextRow.lnk);
      nextRowElement?.scrollIntoView({ block: 'center' });
    });
  };

  const [mainSplitPct, setMainSplitPct] = useState(() => {
    const saved = Number(localStorage.getItem('bse2LayoutMainSplit'));
    return saved >= 25 && saved <= 75 ? saved : 55;
  });
  const [infoPanelHeight, setInfoPanelHeight] = useState(() => {
    const saved = Number(localStorage.getItem('bse3LayoutInfoHeight'));
    return saved >= 120 && saved <= 320 ? saved : 165;
  });
  const workspaceRef = useRef(null);
  const leftPanelRef = useRef(null);
  const tableScrollRef = useRef(null);

  const startColResize = (event) => {
    event.preventDefault();
    const rect = workspaceRef.current?.getBoundingClientRect();
    if (!rect) return;
    document.body.classList.add('resizing-col');
    const onMove = (moveEvent) => {
      const x = moveEvent.clientX - rect.left;
      const pct = Math.min(75, Math.max(25, (x / rect.width) * 100));
      setMainSplitPct(pct);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.classList.remove('resizing-col');
      setMainSplitPct((current) => {
        localStorage.setItem('bse2LayoutMainSplit', String(current));
        return current;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const startLeftRowResize = (event) => {
    event.preventDefault();
    const rect = leftPanelRef.current?.getBoundingClientRect();
    if (!rect) return;
    document.body.classList.add('resizing-row');
    const onMove = (moveEvent) => {
      const height = Math.min(320, Math.max(120, moveEvent.clientY - rect.top));
      setInfoPanelHeight(height);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.classList.remove('resizing-row');
      setInfoPanelHeight((current) => {
        localStorage.setItem('bse3LayoutInfoHeight', String(current));
        return current;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // The mobile toolbar is a horizontally-scrolling strip (overflow-x:auto),
  // which per the CSS spec also clips the y-axis once x is non-visible - so
  // the search results dropdown (position:absolute, meant to spill below
  // the toolbar) was getting silently clipped/hidden there. Positioning it
  // with position:fixed at JS-computed coordinates escapes that ancestor
  // entirely instead of fighting the clipping.
  useEffect(() => {
    if (searchResults.length === 0 || !tickerLookupRef.current) {
      setTickerLookupDropdownStyle(null);
      return undefined;
    }
    const updatePosition = () => {
      if (!tickerLookupRef.current) return;
      const rect = tickerLookupRef.current.getBoundingClientRect();
      setTickerLookupDropdownStyle({ position: 'fixed', top: rect.bottom + 2, left: rect.left, width: rect.width });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [searchResults.length]);

  useEffect(() => {
    apiFetch('/api/groups')
      .then((r) => r.json())
      .then((availableGroups) => {
        const nextGroups = Array.isArray(availableGroups) ? availableGroups : [];
        setGroups(nextGroups);
        setGroup((current) => {
          if (nextGroups.includes(current)) return current;
          const fallback = [DEFAULT_ADMIN_GROUP, 'momentum_merged'].find((g) => nextGroups.includes(g))
            || nextGroups[0] || '';
          if (fallback) writeSession(GROUP_KEY, fallback);
          return fallback;
        });
      })
      .catch(() => setError('Could not load groups'));
  }, []);

  useEffect(() => {
    apiFetch('/api/watchlist')
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('watchlist')))
      .then((body) => setWatchlist(body.items || []))
      .catch(() => {});
  }, []);

  const loadTrading = useCallback(async (silent = false) => {
    if (!canTrade) return null;
    if (!silent) setTradingBusy(true);
    try {
      const response = await apiFetch('/api/trading/state');
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Unable to load trades');
      setTradingState(body);
      setTradingError('');
      return body;
    } catch (requestError) {
      if (!silent) setTradingError(requestError.message);
      return null;
    } finally {
      if (!silent) setTradingBusy(false);
    }
  }, [canTrade]);

  useEffect(() => {
    if (!canTrade) return undefined;
    loadTrading();
    const interval = window.setInterval(() => loadTrading(true), 30000);
    return () => window.clearInterval(interval);
  }, [loadTrading, canTrade]);

  useEffect(() => {
    if (!canTrade && page !== 'screener') setPage('screener');
  }, [canTrade, page]);

  useEffect(() => {
    let cancelled = false;
    const loadStocks = () => {
      setLoading(true); setError('');
      const params = new URLSearchParams();
      if (group) params.set('group', group);
      apiFetch(`/stocks?${params.toString()}`)
        .then((r) => r.json())
        .then((data) => { if (!cancelled) setRows(Array.isArray(data) ? data : []); })
        .catch(() => { if (!cancelled) setError('Unable to load stocks'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    loadStocks();
    return () => { cancelled = true; };
  }, [group]);

  // Update only the live numeric fields. This does not set loading state,
  // replace the selected ticker, reload the chart, or move the scroll position.
  useEffect(() => {
    if (!group || page !== 'screener') return undefined;
    let cancelled = false;
    let inFlight = false;
    const pollLiveValues = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const response = await apiFetch(`/api/stock-prices?group=${encodeURIComponent(group)}`);
        const updates = response.ok ? await response.json() : [];
        if (cancelled || !Array.isArray(updates)) return;
        const byLink = new Map(updates.map((row) => [row.lnk, row]));
        setRows((current) => {
          let changed = false;
          const next = current.map((row) => {
            const update = byLink.get(row.lnk);
            if (!update) return row;
            if (row.r1d === update.r1d && row.atr_pct === update.atr_pct && row.sl === update.sl) return row;
            changed = true;
            return { ...row, r1d: update.r1d, atr_pct: update.atr_pct, sl: update.sl };
          });
          return changed ? next : current;
        });
      } catch (_) {
        // A missed poll is intentionally invisible; the next one retries.
      } finally {
        inFlight = false;
      }
    };
    const interval = window.setInterval(pollLiveValues, 5000);
    return () => { cancelled = true; window.clearInterval(interval); };
  }, [group, page]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    const handle = setTimeout(() => {
      apiFetch(`/api/search?q=${encodeURIComponent(q)}`)
        .then((r) => r.json())
        .then((data) => setSearchResults(Array.isArray(data) ? data : []))
        .catch(() => setSearchResults([]));
    }, 200);
    return () => clearTimeout(handle);
  }, [query]);

  const baseSortedRows = useMemo(() => {
    // `sl` is displayed as a live calculation, so sort on that same value
    // rather than the stored field.
    const sortValue = (row) => {
      if (sort.key === 'sl') return slPercent(row);
      if (sort.key === 'lowReturn') return lowReturnFor(row, group);
      return row[sort.key];
    };
    // ADR filter: rows below the threshold (or with no ADR) are dropped from
    // every list. The ADR column shows the absolute value, so compare on that.
    const minAdr = Number(adrThreshold) || 0;
    const adrRows = minAdr > 0
      ? rows.filter((row) => {
        const adr = Math.abs(Number(row.adr));
        return row.adr !== null && row.adr !== '' && Number.isFinite(adr) && adr >= minAdr;
      })
      : rows;
    return [...adrRows].sort((a, b) => {
      const av = sortValue(a);
      const bv = sortValue(b);
      const aMissing = av === '' || av === null || av === undefined;
      const bMissing = bv === '' || bv === null || bv === undefined;
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      const aNumber = Number(av);
      const bNumber = Number(bv);
      const comparison = Number.isFinite(aNumber) && Number.isFinite(bNumber)
        ? aNumber - bNumber
        : String(av).localeCompare(String(bv));
      return sort.direction === 'asc' ? comparison : -comparison;
    });
  }, [rows, sort, group, adrThreshold]);

  // Live price polls refresh %/ATR/SL every few seconds; re-sorting on them
  // makes rows jump around while reading. So the order is snapshotted once
  // the data loads (and whenever the group or sort changes) and held until
  // then - the cell values keep updating, positions don't move.
  const [frozenOrder, setFrozenOrder] = useState(null);
  useEffect(() => { setFrozenOrder(null); }, [group, sort.key, sort.direction]);
  useEffect(() => {
    if (loading || frozenOrder || baseSortedRows.length === 0) return;
    setFrozenOrder(baseSortedRows.map((row) => `${row.lnk}|${row.grp}`));
  }, [loading, frozenOrder, baseSortedRows]);

  const sortedRows = useMemo(() => {
    if (!frozenOrder) return baseSortedRows;
    const position = new Map(frozenOrder.map((key, i) => [key, i]));
    const end = baseSortedRows.length;
    return [...baseSortedRows].sort((a, b) => {
      const ai = position.has(`${a.lnk}|${a.grp}`) ? position.get(`${a.lnk}|${a.grp}`) : end;
      const bi = position.has(`${b.lnk}|${b.grp}`) ? position.get(`${b.lnk}|${b.grp}`) : end;
      return ai - bi;
    });
  }, [baseSortedRows, frozenOrder]);

  // Live "New" filter: when on, only today's unticked rows are shown.
  const visibleRows = useMemo(() => {
    if (!showOnlyUnticked) return sortedRows;
    return sortedRows.filter((row) => !viewedSymbols.has(row.lnk));
  }, [sortedRows, showOnlyUnticked, viewedSymbols]);

  // Falls back to the list's first row only when sortedRows itself changes
  // (group switch, data reload) and the previous selection fell out of it -
  // NOT on every selectedLink change, or picking a stock via ticker search
  // that isn't part of the current group's rows gets immediately reverted
  // right back (selectedLinkRef avoids that without re-running on every
  // selection).
  const selectedLinkRef = useRef(selectedLink);
  useEffect(() => { selectedLinkRef.current = selectedLink; }, [selectedLink]);
  useEffect(() => {
    // Rows haven't loaded yet (e.g. right after a refresh) - wait rather
    // than wiping out a restored selection just because it's momentarily
    // empty.
    if (sortedRows.length === 0) return;
    if (sortedRows.some((row) => row.lnk === selectedLinkRef.current)) return;
    setSelectedLink(sortedRows[0]?.lnk || '');
  }, [sortedRows]);

  // Remembers the selected row across a page refresh (e.g. tabbing away to
  // look something up and coming back) - restored into the initial state
  // above; the fallback-to-first-row effect just above already leaves a
  // still-valid restored link alone.
  useEffect(() => {
    if (selectedLink) localStorage.setItem('bse2SelectedLink', selectedLink);
  }, [selectedLink]);

  const selectedRow = rows.find((row) => row.lnk === selectedLink) || null;
  const stockRow = selectedRow || searchedStock;
  const selectedSortedIndex = sortedRows.findIndex((row) => row.lnk === selectedLink);



  const changeSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const sortMark = (key) => (sort.key !== key ? '' : (sort.direction === 'asc' ? ' ↑' : ' ↓'));

  const changeGroup = (nextGroup) => {
    writeSession(GROUP_KEY, nextGroup);
    setGroup(nextGroup);
  };

  // Keeps the common destinations available without making a phone user open
  // the full settings toolbar first. Group destinations stay on the screener
  // page; trading destinations are only shown to users who can trade.
  const launchScreen = (nextPage, nextGroup) => {
    setPage(nextPage);
    writeSession(PAGE_KEY, nextPage);
    if (nextGroup) changeGroup(nextGroup);
    setToolbarOpen(false);
    setScreenLauncherOpen(false);
  };

  const selectStock = (link) => {
    setSelectedLink(link);
    setStopPickMode(false);
    setQuery('');
    setSearchResults([]);
    const known = rows.some((row) => row.lnk === link);
    if (known) {
      setSearchedStock(null);
      return;
    }
    apiFetch(`/stocks/${encodeURIComponent(link)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('not found'))))
      .then((detail) => setSearchedStock(detail || null))
      .catch(() => setSearchedStock(null));
  };

  // Pre-fetch the previous/next scripts' charts (both variants) so stepping
  // through rows renders instantly instead of waiting on fresh downloads.
  useEffect(() => {
    const idx = selectedSortedIndex;
    if (idx < 0 || !sortedRows.length) return;
    const prevLink = sortedRows[idx - 1]?.lnk;
    const nextLink = sortedRows[idx + 1]?.lnk;
    if (prevLink) prefetchChart(prevLink, chartPeriod);
    if (nextLink) prefetchChart(nextLink, chartPeriod);
  }, [selectedSortedIndex, sortedRows, chartPeriod]);

  // Pre-fetch news for the next tickers so clicking Next shows news instantly.
  // Goes through the newsCache (keyed by ticker id), never the bare browser
  // HTTP cache, so the data NewsPanel renders is always for the requested
  // ticker - a prefetched response can never surface as another ticker's news.
  useEffect(() => {
    const idx = selectedSortedIndex;
    if (idx < 0 || !sortedRows.length) return;
    [sortedRows[idx + 1], sortedRows[idx + 2]].forEach((row) => {
      if (!row) return;
      const id = row.bse || row.lnk;
      prefetchNews(id, apiFetch);
    });
  }, [selectedSortedIndex, sortedRows]);

  const toggleWatchlist = async () => {
    if (!selectedLink || watchlistBusy) return;
    setWatchlistBusy(true);
    setWatchlistMessage('');
    try {
      const response = await apiFetch('/api/watchlist', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol: selectedLink, action: 'toggle' }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Unable to update watchlist');
      setWatchlist(body.items || []);
      setWatchlistMessage(body.added ? 'Added to watchlist' : 'Removed from watchlist');
      window.setTimeout(() => setWatchlistMessage(''), 4000);
    } catch (requestError) {
      setWatchlistMessage(requestError.message);
    } finally {
      setWatchlistBusy(false);
    }
  };

  // Ask the scraper to re-pull this company from screener.in, then reload the
  // chart (and the figures under it) with the fresh data. Used when the
  // fundamentals look wrong or stale.
  const refreshStock = async () => {
    if (!isAdmin || !selectedLink || refreshing) return;
    setRefreshing(true);
    setRefreshMsg('');
    try {
      const response = await apiFetch(`/api/rescrape/${encodeURIComponent(selectedLink)}`, { method: 'POST' });
      const body = await response.json().catch(() => ({}));
      setRefreshMsg(body.message || (body.success ? 'Refreshed' : 'Refresh failed'));
      if (body.success) setChartReloadKey((key) => key + 1);
    } catch (err) {
      setRefreshMsg('Refresh failed — check server logs');
    } finally {
      setRefreshing(false);
      setTimeout(() => setRefreshMsg(''), 6000);
    }
  };

  const moveSelection = (step) => {
    if (!sortedRows.length) return;
    const currentIndex = selectedSortedIndex >= 0 ? selectedSortedIndex : (step > 0 ? -1 : sortedRows.length);
    const nextIndex = Math.max(0, Math.min(sortedRows.length - 1, currentIndex + step));
    if (nextIndex === selectedSortedIndex) return;
    setSelectedLink(sortedRows[nextIndex].lnk);
    window.requestAnimationFrame(() => {
      const row = tableScrollRef.current?.querySelectorAll('tbody tr')?.[nextIndex];
      row?.scrollIntoView({ block: 'center' });
    });
  };

  const tradingRequest = async (url, init, successMessage = '') => {
    setTradingBusy(true);
    setTradingError('');
    setTradingMessage('');
    try {
      const response = await apiFetch(url, init);
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Trade journal request failed');
      if (body.summary) setTradingState(body);
      else await loadTrading(true);
      if (successMessage) {
        setTradingMessage(successMessage);
        window.setTimeout(() => setTradingMessage(''), 5000);
      }
      return true;
    } catch (requestError) {
      setTradingError(requestError.message);
      return false;
    } finally {
      setTradingBusy(false);
    }
  };

  const recordTrade = ({ symbol, brokerId, quantity, entryPrice, stopPrice, filled }) => tradingRequest(
    '/api/trading/trades',
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ symbol, brokerId, quantity, entryPrice, stopPrice, filled }) },
    `${symbol} trade recorded`
  );

  const fillTrade = (tradeId) => tradingRequest(
    `/api/trading/trades/${tradeId}/fill`,
    { method: 'POST' },
    'Order marked as filled'
  );

  const updateStop = (tradeId, stopPrice) => tradingRequest(
    `/api/trading/trades/${tradeId}/stop`,
    { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stopPrice }) },
    'Stop loss updated'
  );

  const correctTrade = (tradeId, quantity, entryPrice) => tradingRequest(
    `/api/trading/trades/${tradeId}`,
    { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ quantity, entryPrice }) },
    'Position corrected'
  );

  const closeTrade = (tradeId, quantity, exitPrice) => tradingRequest(
    `/api/trading/trades/${tradeId}/close`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ quantity, exitPrice }) },
    'Broker close recorded'
  );

  const voidTrade = async (tradeId) => {
    if (!window.confirm('Void this entry error? It will disappear from positions and all statistics.')) return false;
    return tradingRequest(`/api/trading/trades/${tradeId}`, { method: 'DELETE' }, 'Trade voided');
  };

  const unvoidTrade = (tradeId) => tradingRequest(
    `/api/trading/trades/${tradeId}/unvoid`,
    { method: 'POST' },
    'Position restored'
  );

  const addBroker = (name) => tradingRequest(
    '/api/trading/brokers',
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) },
    'Broker added'
  );

  const updateBroker = (brokerId, changes) => tradingRequest(
    `/api/trading/brokers/${brokerId}`,
    { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(changes) },
    'Broker updated'
  );

  const isWatchlisted = watchlist.some((item) => item.symbol === selectedLink);
  // Phone-only: the toolbar collapses behind a floating button so the chart
  // gets the whole screen. The CSS only applies under 760px, so desktop keeps
  // the toolbar exactly as it was.

  return (
    <div className="app-root">
      <DataHealthBanner />
      <WatchlistAlerts onOpenStock={(link) => { launchScreen('screener', 'watchlist'); selectStock(link); }} />
      <DraggableFab
        label={toolbarOpen ? '\u2715' : '\u2630'}
        className="floating-menu-button"
        storageKey="bse5-menu-fab-pos"
        onAction={() => {
          setScreenLauncherOpen(false);
          setToolbarOpen((current) => !current);
        }}
      />
      <DraggableFab
        label={screenLauncherOpen ? '\u2715' : 'Screens'}
        className="floating-screens-button"
        storageKey="bse5-screens-fab-pos"
        onAction={() => {
          setToolbarOpen(false);
          setScreenLauncherOpen((current) => !current);
        }}
      />
      {(toolbarOpen || screenLauncherOpen) && (
        <div
          className="toolbar-backdrop"
          role="presentation"
          onPointerDown={() => {
            setToolbarOpen(false);
            setScreenLauncherOpen(false);
          }}
        />
      )}
      {screenLauncherOpen && (
        <nav className="screen-launcher" aria-label="Open screen">
          <span className="screen-launcher-title">Quick screens</span>
          <button type="button" className={page === 'screener' && group === 'news' ? 'active' : ''} onClick={() => launchScreen('screener', 'news')}>News</button>
          <button type="button" className={page === 'screener' && group === 'momentum_merged' ? 'active' : ''} onClick={() => launchScreen('screener', 'momentum_merged')}>Momentum</button>
          <button type="button" className={page === 'screener' && group === 'momentum_coiled' ? 'active' : ''} onClick={() => launchScreen('screener', 'momentum_coiled')}>Coiled</button>
          <button type="button" className={page === 'screener' && group === 'watchlist' ? 'active' : ''} onClick={() => launchScreen('screener', 'watchlist')}>Watchlist</button>
          {canTrade && <>
            <button type="button" className={page === 'positions' ? 'active' : ''} onClick={() => launchScreen('positions')}>Holdings</button>
          </>}
          <button type="button" className={page === 'screener' && group === 'RsltDeclrd' ? 'active' : ''} onClick={() => launchScreen('screener', 'RsltDeclrd')}>RsltDeclrd</button>
          <button type="button" className={page === 'screener' && group === 'ResultsAntici' ? 'active' : ''} onClick={() => launchScreen('screener', 'ResultsAntici')}>ResultsAntici</button>
          <>
            <span className="screen-launcher-divider" />
            <button type="button" className={page === 'screener' && group === 'all_tickers' ? 'active' : ''} onClick={() => launchScreen('screener', 'all_tickers')}>Screener</button>
          </>
          {canTrade && <>
            <button type="button" className={page === 'orders' ? 'active' : ''} onClick={() => launchScreen('orders')}>Orders</button>
            <button type="button" className={page === 'closed' ? 'active' : ''} onClick={() => launchScreen('closed')}>Closed trades</button>
          </>}
        </nav>
      )}
      <header className={`app-toolbar collapsible${toolbarOpen ? ' open' : ''}`}>
        <button
          type="button"
          className="paper-user-input authenticated-user"
          aria-label="Open profile"
          title="Profile, brokers and risk per trade"
          onClick={() => setShowProfile(true)}
        >
          {username} ▾
        </button>
        <button type="button" className="toolbar-auth-button" onClick={onLogout}>Logout</button>

        <select className="page-select" aria-label="Page" value={page} onChange={(event) => setPage(event.target.value)}>
          <option value="screener">Screener</option>
          {canTrade && <option value="positions">Live positions</option>}
          {canTrade && <option value="orders">Orders{tradingState.orders?.length ? ` (${tradingState.orders.length})` : ''}</option>}
          {canTrade && <option value="closed">Closed trades</option>}
        </select>

        {canTrade && <div className="account-strip" aria-label="Trade summary">
          <span><small>Total P/L</small><strong className={tradingState.summary.total_pnl >= 0 ? 'positive' : 'negative'}>{tradingState.summary.total_pnl >= 0 ? '+' : '-'}₹{numberText(Math.abs(tradingState.summary.total_pnl))}</strong></span>
          <span><small>Successful trades</small><strong>{tradingState.summary.wins} ({numberText(tradingState.summary.success_rate, 1)}%)</strong></span>
        </div>}

        {page === 'screener' && (
          <>
            <select
              className="finviz-group-select"
              aria-label="Group"
              value={group}
              onChange={(event) => changeGroup(event.target.value)}
            >
              {groups.map((g) => <option key={g} value={g}>{g === 'all_tickers' ? 'All' : g === 'watchlist' ? 'My watchlist' : g === 'news' ? 'News' : g}</option>)}
            </select>
            {isSpringoutGroup && springAsOf && (
              <span className="coil-asof" title="At least 3 sessions in a 30-day window closing above the 10MA, then 2 or more sessions back at the line (within +1% above or 10% below it, or straddling it), then a 1.5 ATR candle closing back above it. The coil is measured to the last settled session; today's candle can be the spring.">
                10MA return to {springAsOf}
              </span>
            )}
            {isCoiledGroup && coilAsOf && (
              <span className="coil-asof" title="The newest session's high and low are not final, so the coil is measured to the last settled session. The % column stays live.">
                coil to {coilAsOf}
              </span>
            )}

            <button
              type="button"
              className={`viewed-filter-button ${showOnlyUnticked ? 'active' : ''}`}
              aria-pressed={showOnlyUnticked}
              onClick={() => setShowOnlyUnticked((current) => !current)}
              title="Show only tickers not yet viewed today"
            >
              New
            </button>

            <label className="adr-filter" title="Only show stocks with ADR% at or above this value">
              ADR ≥
              <input
                type="number"
                step="0.5"
                min="0"
                value={adrThreshold}
                onChange={(event) => setAdrThreshold(Number(event.target.value) || 0)}
              />
            </label>

            <div className="ticker-lookup" ref={tickerLookupRef}>
              <input
                type="text"
                value={query}
                placeholder="Search stocks…"
                onChange={(event) => setQuery(event.target.value)}
              />
              {searchResults.length > 0 && tickerLookupDropdownStyle && createPortal(
                <div className="ticker-lookup-results" style={tickerLookupDropdownStyle}>
                  {searchResults.map((result) => (
                    <button key={result.link} type="button" onClick={() => selectStock(result.link)}>
                      {result.name} <span>{result.link}</span>
                    </button>
                  ))}
                </div>,
                document.body
              )}
            </div>
          </>
        )}
      </header>

      {showProfile && (
        <BrokerProfile
          brokers={tradingState.brokers}
          busy={tradingBusy}
          error={tradingError}
          isAdmin={isAdmin}
          canTrade={canTrade}
          riskPerTrade={riskPerTrade}
          onUpdated={onUpdated}
          onAdd={addBroker}
          onUpdate={updateBroker}
          onManageUsers={onManageUsers}
          onClose={() => setShowProfile(false)}
        />
      )}

      {page === 'positions' ? (
        <PositionsPage state={tradingState} busy={tradingBusy} error={tradingError} onCorrect={correctTrade} onUpdateStop={updateStop} onClose={closeTrade} onVoid={voidTrade} onUnvoid={unvoidTrade} />
      ) : page === 'orders' ? (
        <OrdersPage state={tradingState} busy={tradingBusy} error={tradingError} onFill={fillTrade} onVoid={voidTrade} />
      ) : page === 'closed' ? (
        <ClosedTradesPage state={tradingState} />
      ) : (
      <>
      <main
        className="workspace"
        ref={workspaceRef}
        style={{ gridTemplateColumns: `${mainSplitPct}fr 7px ${100 - mainSplitPct}fr` }}
      >
        <section
          className="left-panel"
          ref={leftPanelRef}
          style={{ gridTemplateRows: `minmax(0, 1fr) 7px ${infoPanelHeight}px` }}
        >
          <div className="chart-side">
            <div className="chart-view-controls">
              {(refreshMsg || watchlistMessage) && <span className="refresh-msg">{refreshMsg || watchlistMessage}</span>}
              <div className="chart-view-toggle chart-engine-toggle" aria-label="Chart engine">
                <button type="button" className={chartMode === 'interactive' ? 'active' : ''} onClick={() => setChartMode('interactive')} title="TradingView interactive chart">TV</button>
                <button type="button" className={chartMode === 'chartdirector' ? 'active' : ''} onClick={() => setChartMode('chartdirector')} title="ChartDirector chart">CD</button>
              </div>
              <div className="chart-control-row">
                <button
                type="button"
                className="chart-refresh-button"
                onClick={refreshStock}
                disabled={!isAdmin || !selectedLink || refreshing}
                title={isAdmin ? 'Rescrape this company from screener.in' : 'Refresh is available only to Edward'}
                >
                {refreshing ? '…' : '↻'}
                </button>
                <button
                type="button"
                className={`chart-ma-toggle ${showFigures ? 'active' : ''}`}
                aria-pressed={showFigures}
                onClick={() => setShowFigures((shown) => !shown)}
                title="Show or hide fundamentals"
                >
                F
                </button>
              </div>
              <div className="chart-view-toggle" aria-label="Chart period">
                <button type="button" className={chartPeriod === 'daily' ? 'active' : ''} onClick={() => setChartPeriod('daily')} title="Daily chart">D</button>
                <button type="button" className={chartPeriod === 'monthly' ? 'active' : ''} onClick={() => setChartPeriod('monthly')} title="Monthly chart">M</button>
              </div>
              <button
                type="button"
                className={`chart-watchlist-button ${isWatchlisted ? 'active' : ''}`}
                onClick={toggleWatchlist}
                disabled={!selectedLink || watchlistBusy}
                title={isWatchlisted ? 'Remove from watchlist' : 'Add to watchlist'}
              >
                {isWatchlisted ? '★' : '☆'}
              </button>
              {isAdmin && (
                <button
                  type="button"
                  className={`chart-watchlist-button ${showSplitBox ? 'active' : ''}`}
                  aria-pressed={showSplitBox}
                  onClick={() => setShowSplitBox((shown) => !shown)}
                  disabled={!selectedLink}
                  title="Adjust older candles for a split or bonus"
                >
                  S
                </button>
              )}
              {isAdmin && showSplitBox && selectedLink && (
                <SplitAdjustBox
                  link={selectedLink}
                  onClose={() => setShowSplitBox(false)}
                  onChanged={() => {
                    invalidateChart(selectedLink);
                    setChartReloadKey((key) => key + 1);
                  }}
                />
              )}
              {canTrade && (
                <button
                  type="button"
                  className={`chart-watchlist-button ${showOrderBox ? 'active' : ''}`}
                  aria-pressed={showOrderBox}
                  onClick={toggleOrderBox}
                  title={showOrderBox ? 'Hide the order box' : 'Show the order box'}
                >
                  O
                </button>
              )}
              {selectedLink && (
                <a
                  className="chart-screener-link"
                  href={stockRow?.sclink || `https://www.screener.in/company/${encodeURIComponent(selectedLink)}/consolidated/`}
                  target="_blank"
                  rel="noreferrer"
                  title="Open on screener.in"
                >
                  Screener
                </a>
              )}
              {selectedLink && (
                <a
                  className="chart-screener-link"
                  href={`https://chartink.com/stocks/${encodeURIComponent(selectedLink)}.html`}
                  target="_blank"
                  rel="noreferrer"
                  title="Open on chartink.com"
                >
                  ChartInk
                </a>
              )}
            </div>
            {selectedLink
              ? (
                <>
                  <div
                    className={[
                      'chart-body',
                      canTrade ? 'chart-body-with-order' : '',
                      showFigures ? 'chart-body-hidden' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <div className="chart-engine-slot">
                      {chartMode === 'interactive'
                        ? (
                          <InteractiveChart
                            link={selectedLink}
                            period={chartPeriod}
                            reloadKey={chartReloadKey}
                            pickMode={stopPickMode ? 'stop' : ''}
                            entryPrice={orderEntryPrice}
                            stopPrice={orderStopPrice}
                            onPricePick={(price) => {
                              setOrderStopPrice(Number(price).toFixed(2));
                              setStopPickMode(false);
                            }}
                          />
                        )
                        : (
                          <ChartPanel
                            link={selectedLink}
                            period={chartPeriod}
                            reloadKey={chartReloadKey}
                            onToggleDetails={() => setShowFigures((shown) => !shown)}
                            pickMode={stopPickMode ? 'stop' : ''}
                            onPricePick={(price) => {
                              setOrderStopPrice(Number(price).toFixed(2));
                              setStopPickMode(false);
                            }}
                          />
                        )}
                    </div>
                    <FundamentalsChart
                      link={selectedLink}
                      period={chartPeriod}
                      reloadKey={chartReloadKey}
                      onClick={() => setShowFigures((shown) => !shown)}
                    />
                  </div>
                  {showFigures && (
                    <div
                      className={`chart-figures-drawer ${canTrade ? 'chart-figures-drawer-with-order' : ''}`}
                      onClick={() => setShowFigures(false)}
                      title="Click to show the chart again"
                    >
                      <FinancialsTable link={selectedLink} reloadKey={chartReloadKey} />
                    </div>
                  )}
                </>
              )
              : <div className="chart-empty">Select a stock</div>}
            {canTrade && showOrderBox && (
              <DraggableBox className="chart-order-overlay" storageKey="bse4-order-box-pos">
                <QuickOrderBar
                  symbol={selectedLink}
                  brokers={tradingState.brokers}
                  busy={tradingBusy}
                  error={tradingError}
                  message={tradingMessage}
                  entryPrice={orderEntryPrice}
                  setEntryPrice={setOrderEntryPrice}
                  stopPrice={orderStopPrice}
                  setStopPrice={setOrderStopPrice}
                  stopPickMode={stopPickMode}
                  onToggleStopPick={() => setStopPickMode((active) => !active)}
                  onRecord={recordTrade}
                  riskPerTrade={riskPerTrade}
                />
              </DraggableBox>
            )}
          </div>
          <div className="resize-handle row" onMouseDown={startLeftRowResize} />
          <StockInfoPanel
            link={selectedLink}
            canEditAnalysis={isAdmin}
            name={stockRow?.nm}
            sclink={stockRow?.sclink || (selectedLink ? `https://www.screener.in/company/${encodeURIComponent(selectedLink)}/consolidated/` : '')}
            bseCode={stockRow?.bse}
            nseCode={stockRow?.nse}
            onPrevious={() => moveSelection(-1)}
            onNext={() => moveSelection(1)}
            canPrevious={selectedSortedIndex > 0}
            canNext={selectedSortedIndex >= 0 && selectedSortedIndex < sortedRows.length - 1}
            position={selectedSortedIndex >= 0 ? selectedSortedIndex + 1 : 0}
            total={sortedRows.length}
          />
        </section>

        <div className="resize-handle col" onMouseDown={startColResize} />

        <section className="table-panel">
          <section className="screener-section">
            {error && <div className="table-message error">{error}</div>}
            {loading && <div className="table-message">Loading…</div>}
            {!loading && !error && visibleRows.length === 0 && <div className="table-message">{showOnlyUnticked ? 'All viewed for today' : 'No matches'}</div>}
            {visibleRows.length > 0 && (
              <div className="table-scroll" ref={tableScrollRef}>
                <table className="results-table">
                  <thead>
                    <tr>
                      <th className="col-symbol" onClick={() => changeSort('nm')}>Name{sortMark('nm')}</th>
                      <th className="number col-1d" onClick={() => changeSort('r1d')}>%{sortMark('r1d')}</th>
                      {group === 'watchlist' ? (
                        <th className="number col-lowret" title="Below 10MA: the latest price is under the average of the last 10 daily closes" onClick={() => changeSort('b10')}>b10{sortMark('b10')}</th>
                      ) : (
                        <th className="number col-lowret" onClick={() => changeSort('lowReturn')}>{lowReturnLabelFor(group)}{sortMark('lowReturn')}</th>
                      )}
                      <th className="number col-adr" onClick={() => changeSort('adr')}>ADR{sortMark('adr')}</th>
                      <th className="number col-atrsl" title="ATR as % of price / remaining room after today's move" onClick={() => changeSort('sl')}>ATR/SL{sortMark('sl')}</th>
                      <th className="number col-rvol" title="Relative volume: how much this stock has traded against what it normally trades BY THIS POINT IN THE SESSION. While the market is open that is today's volume so far against the median for the same 5-minute slice of previous sessions; otherwise it is the last completed session against the median session. 1.0x is a normal day."
                        onClick={() => changeSort('rvol')}>RVol{sortMark('rvol')}</th>
                      <th className="number col-cf" onClick={() => changeSort('net_cashflow')}>CF{sortMark('net_cashflow')}</th>
                      <th className="col-eg" onClick={() => changeSort('equity_growth')}>EqGr{sortMark('equity_growth')}</th>
                      <th className="col-cap" onClick={() => changeSort('major_capex')}>Cap{sortMark('major_capex')}</th>
                      {group === 'concall' ? (
                        <>
                          <th className="col-action" onClick={() => changeSort('at')}>Action{sortMark('at')}</th>
                          <th className="col-condt" onClick={() => changeSort('ccd')}>ConDt{sortMark('ccd')}</th>
                        </>
                      ) : (
                        <th className="number col-mcap" onClick={() => changeSort('m_c')}>MCap{sortMark('m_c')}</th>
                      )}
                      {/* Derived groups carry the measurements that explain why
                          a row qualified. */}
                      {isSpringoutGroup && (
                        <>
                          <th className="number col-range" title="The spring candle: the advance out of the coil measured in ATRs, closing back above the 10MA. A dot marks today's still-forming candle; without one the spring is the last settled close. A dash means the name is still coiling."
                            onClick={() => changeSort('spring_atrs')}>Spring{sortMark('spring_atrs')}</th>
                          <th className="number col-range" title="The run: sessions in the 30 before the coil that closed above the 10MA, and how far above the line it got at its peak. Counted, not consecutive - 3 or more qualifies."
                            onClick={() => changeSort('ext_days')}>Ext{sortMark('ext_days')}</th>
                          <th className="number col-range" title="The return to the line: consecutive sessions closing within +1% / -10% of the 10MA, or trading through it. 2 or more qualifies. T = sessions that traded through the line, B = sessions that closed below it."
                            onClick={() => changeSort('coil_days')}>Coil{sortMark('coil_days')}</th>
                          <th className="number col-range" title="Base quality, reported but never used to filter. Contraction = average true range during the coil against the extension leg: under 1.00x means the stock went quiet while it hugged the line, which is what a real base looks like. q = sessions in the coil whose body was 30% or less of the stock's normal pre-coil range - the nothing days."
                            onClick={() => changeSort('coil_contraction')}>Base{sortMark('coil_contraction')}</th>
                        </>
                      )}
                      {isCoiledGroup && (
                        <>
                          <th className="number col-range" title="High-to-low range of the tightest two sessions in the last 10, as a % of that day's close. They need not be consecutive."
                            onClick={() => changeSort('range_1d')}>Coil days{sortMark('range_1d')}</th>
                          <th className="number col-range" title="The wider of those two days against this stock's own 20-day average range. Lower is tighter."
                            onClick={() => changeSort('range_ratio')}>Coil{sortMark('range_ratio')}</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row, index) => (
                      <tr
                        key={`${row.lnk}-${row.grp}-${index}`}
                        data-link={row.lnk}
                        className={[
                          row.lnk === selectedLink ? 'selected' : '',
                          viewedSymbols.has(row.lnk) ? 'row-viewed' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => selectStock(row.lnk)}
                      >
                        <td className="symbol col-symbol">
                          <span className="symbol-name">{row.nm}</span>
                          {FNO_TICKERS.has(row.lnk) && <span className="fno-badge">F&O</span>}
                          <button
                            type="button"
                            className={`tick-btn ${viewedSymbols.has(row.lnk) ? 'ticked' : ''}`}
                            aria-pressed={viewedSymbols.has(row.lnk)}
                            title={viewedSymbols.has(row.lnk) ? 'Mark as not viewed' : 'Mark as viewed'}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleViewed(row.lnk);
                            }}
                          >
                            ✓
                          </button>
                        </td>
                        <td className={`number col-1d ${Number(row.r1d) >= 0 ? 'positive' : 'negative'}`}>
                          {Number(row.r1d) >= 0 ? '+' : ''}{numberText(row.r1d)}%
                        </td>
                        {group === 'watchlist' ? (
                          <td className={`number col-lowret${row.b10 ? ' negative' : ''}`} title={row.ma10 == null ? 'Fewer than 10 daily candles' : `10MA ${row.ma10}`}>{row.b10 == null ? '—' : row.b10 ? 'True' : 'False'}</td>
                        ) : (
                          <td className="number col-lowret">{lowReturnFor(row, group) == null ? '' : `${numberText(lowReturnFor(row, group), 0)}%`}</td>
                        )}
                        <td className="number col-adr">{row.adr == null || row.adr === '' ? '' : `${numberText(Math.abs(Number(row.adr)), 0)}%`}</td>
                        <td className="number atr-sl-cell col-atrsl">
                          <span title={`ATR ${numberText(row.atr_abs, 2)}`}>{numberText(row.atr_pct, 0)}</span>
                          <span className="atr-sl-separator">/</span>
                          <span className={slPercent(row) >= 0 ? 'positive' : 'negative'}>
                            {numberText(slPercent(row), 0)}
                          </span>
                        </td>
                        <td className={`number col-rvol${Number(row.rvol) >= 2 ? ' rvol-heavy' : ''}`}
                          title={row.rvol == null
                            ? 'No volume baseline for this stock yet'
                            : row.rvol_stage === 'live'
                              ? `${row.rvol}x normal volume by this point in today's session`
                              : `${row.rvol}x the median session, measured on the last completed day`}>
                          {row.rvol == null ? '—' : `${Number(row.rvol).toFixed(1)}x`}
                        </td>
                        <td className="number col-cf">{row.net_cashflow ? volumeText(row.net_cashflow) : '—'}</td>
                        <td className="col-eg">{row.equity_growth ? '✓' : ''}</td>
                        <td className="col-cap">{row.major_capex ? 'M' : ''}</td>
                        {group === 'concall' ? (
                          <>
                            <td className="col-action">{row.at ? String(row.at).replace(/^View\s+/i, '').slice(0, 18) : ''}</td>
                            <td className="col-condt">{row.ccd || ''}</td>
                          </>
                        ) : (
                          <td className={`number col-mcap ${Number(row.m_c) > 50000 ? 'high-market-cap' : ''}`}>{volumeText(row.m_c)}</td>
                        )}
                        {isSpringoutGroup && (
                          <>
                            <td className={`number col-range${row.spring ? ' spring-fired' : ''}`}
                              title={row.spring
                                ? `Spring on ${row.spring_date} (${row.spring_stage === 'live' ? "today's candle, still forming" : 'settled close'}): +${row.spring_pct}%, ${row.spring_atrs} ATR, closing ${row.spring_above_ma_pct}% above the 10MA`
                                  + `${row.spring_rvol != null ? `, ${row.spring_rvol}x relative volume${row.spring_rvol_ok === false ? ' (light - not a disqualifier)' : ''}` : ', relative volume unknown'}`
                                : 'Still coiling - no spring candle yet'}>
                              {row.spring ? `${Number(row.spring_atrs).toFixed(1)}×${row.spring_stage === 'live' ? ' •' : ''}` : '—'}
                            </td>
                            <td className="number col-range"
                              title={row.ext_from
                                ? `${row.ext_days} sessions above the 10MA between ${row.ext_from} and ${row.ext_to} (longest unbroken run ${row.ext_longest_run}), peaking ${row.ext_max_pct}% above the line`
                                : ''}>
                              {row.ext_days != null ? `${row.ext_days}d +${Number(row.ext_max_pct).toFixed(1)}%` : '—'}
                            </td>
                            <td className="number col-range" title={`10MA ${row.ma10 ?? '—'}; coiled ${row.coil_from ?? '—'} to ${row.coil_to ?? '—'}, ${row.coil_touch_days ?? 0} traded through the line, ${row.coil_below_days ?? 0} closed below it, straying at most ${row.coil_max_abs_pct ?? '—'}% away`}>
                              {row.coil_days != null ? `${row.coil_days}d${row.coil_touch_days ? ` / ${row.coil_touch_days}T` : ''}${row.coil_below_days ? ` / ${row.coil_below_days}B` : ''}` : '—'}
                            </td>
                            <td className="number col-range"
                              title={row.coil_contraction != null
                                ? `Coil ATR ${row.coil_atr_pct}% of price vs ${row.ext_atr_pct}% on the extension leg`
                                  + ` (${Number(row.coil_contraction).toFixed(2)}x), ${row.coil_tiny_days ?? 0} near-bodyless sessions`
                                : 'No contraction reading'}>
                              {row.coil_contraction != null
                                ? `${Number(row.coil_contraction).toFixed(2)}x${row.coil_tiny_days ? ` / ${row.coil_tiny_days}q` : ''}`
                                : '—'}
                            </td>
                          </>
                        )}
                        {isCoiledGroup && (
                          <>
                            <td className="number col-range"
                              title={row.coil_from
                                ? `Tightest sessions: ${row.coil_from}${row.coil_to && row.coil_to !== row.coil_from ? ` and ${row.coil_to}` : ''}`
                                  + `\nMeasured on the ${row.coil_measure === 'body' ? 'candle body (open to close)' : 'full high-to-low range'}`
                                  + `${row.coil_measure === 'body' ? ` - ADR ${row.adr_used} is high enough that the wicks drown out the pause` : ''}`
                                : ''}>
                              {(row.coil_ranges || []).map((value) => `${value}%`).join(' / ') || '—'}
                            </td>
                            <td className="number col-range" title={`Normal ${row.coil_measure === 'body' ? 'body' : 'range'} over the 20 sessions behind the coil: ${row.average_range ?? '—'}%`}>
                              {row.range_ratio != null ? `${Number(row.range_ratio).toFixed(2)}x` : '—'}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </section>
      </main>
      </>
      )}
    </div>
  );
};

export default MainApp;
